import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';
import SiteFooter from '../components/SiteFooter';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import NotFoundPage from '../NotFoundPage';
import { BOOKING_URL } from '../tools/stackQuiz';
import { LEAD_STORAGE_KEY, getWhitePaper, isValidEmail, utmFromSearch } from './whitePapers';
import { submitLead } from '../leads/submitLead';
import { track } from '../lib/analytics';
import type { WhitePaper } from './whitePapers';

const SITE_URL = 'https://www.greyfielddata.com';

type Status = 'idle' | 'submitting' | 'done' | 'returning';

function readStoredEmail(): string {
  try {
    return window.localStorage.getItem(LEAD_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function storeEmail(email: string) {
  try {
    window.localStorage.setItem(LEAD_STORAGE_KEY, email);
  } catch {
    // Storage can be blocked; the download still works.
  }
}

function clearStoredEmail() {
  try {
    window.localStorage.removeItem(LEAD_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

function startDownload(href: string) {
  const link = document.createElement('a');
  link.href = href;
  link.download = '';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function leadContext(paper: WhitePaper) {
  return {
    paper: paper.slug,
    paperTitle: paper.title,
    page: window.location.href,
    referrer: document.referrer || '',
    utm: utmFromSearch(window.location.search),
  };
}

const inputClass =
  'h-12 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-base text-slate-950 focus:border-[#1a4e94] focus:outline-none focus:ring-2 focus:ring-[#1a4e94]/30';

function LeadForm({ paper }: { paper: WhitePaper }) {
  const [status, setStatus] = React.useState<Status>('idle');
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [error, setError] = React.useState('');
  const [storedEmail, setStoredEmail] = React.useState('');

  React.useEffect(() => {
    const saved = readStoredEmail();
    if (saved && isValidEmail(saved)) {
      setStoredEmail(saved);
      setStatus('returning');
    }
  }, []);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!isValidEmail(cleanEmail)) {
      setError('Enter a valid work email, like you@brand.com.');
      return;
    }
    setError('');
    setStatus('submitting');
    const result = await submitLead({ email: cleanEmail, name: name.trim(), company: company.trim(), website, ...leadContext(paper) });
    if (result.status === 400) {
      setError(result.error || 'Check your email address and try again.');
      setStatus('idle');
      return;
    }
    // A network or server problem should not keep the paper from the reader.
    if (!website) track('generate_lead', { form: 'white_paper', paper: paper.slug });
    storeEmail(cleanEmail);
    setStoredEmail(cleanEmail);
    setStatus('done');
    track('file_download', { paper: paper.slug, file_name: paper.pdf, method: 'auto' });
    startDownload(paper.pdf);
  }

  function trackDownload(method: string) {
    track('file_download', { paper: paper.slug, file_name: paper.pdf, method });
  }

  function returningDownload() {
    trackDownload('returning');
    // Let Linus know a known reader took this paper too. Fire and forget.
    void submitLead({ email: storedEmail, returning: true, ...leadContext(paper) }, { keepalive: true });
  }

  function useDifferentEmail() {
    clearStoredEmail();
    setStoredEmail('');
    setStatus('idle');
  }

  const downloadButton = (onClick?: () => void) => (
    <a
      href={paper.pdf}
      download
      onClick={onClick}
      className="mt-4 flex min-h-[52px] items-center justify-center rounded-xl bg-[#1a4e94] px-5 font-semibold text-white transition hover:bg-[#153f78]"
    >
      Download the PDF
    </a>
  );

  if (status === 'done') {
    return (
      <div className="mt-6">
        <div role="status" className="rounded-2xl bg-[#e6f4ec] p-5">
          <p className="font-semibold text-[#14532d]">Thanks. Your download has started.</p>
          <p className="mt-1.5 text-[15px] leading-6 text-[#14532d]">If it did not start, use the button below.</p>
        </div>
        {downloadButton(() => trackDownload('button'))}
        <p data-track-location="white_paper_thanks" className="mt-5 text-[15px] leading-6 text-slate-600">
          {paper.next.text}{' '}
          <Link to={paper.next.href} className="font-semibold text-[#1a4e94] underline underline-offset-4">{paper.next.label}</Link>, or{' '}
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1a4e94] underline underline-offset-4">book a free call</a>.
        </p>
      </div>
    );
  }

  if (status === 'returning') {
    return (
      <div className="mt-6">
        <p className="text-[15px] leading-6 text-slate-600">
          Welcome back. We already have your email (<span className="font-semibold text-slate-900">{storedEmail}</span>), so the paper is ready.
        </p>
        {downloadButton(returningDownload)}
        <button type="button" onClick={useDifferentEmail} className="mt-3 min-h-[44px] text-sm font-semibold text-slate-600 underline underline-offset-4 hover:text-[#1a4e94]">
          Not you? Use a different email
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mt-6 space-y-4" aria-describedby="lead-privacy">
      <div>
        <label htmlFor="lead-email" className="text-sm font-semibold text-slate-900">Work email</label>
        <input
          id="lead-email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          placeholder="you@brand.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'lead-error' : undefined}
          className={`mt-1.5 ${inputClass}`}
        />
        {error && <p id="lead-error" role="alert" className="mt-2 text-sm font-medium text-red-700">{error}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="lead-name" className="text-sm font-semibold text-slate-900">Name <span className="font-normal text-slate-500">(optional)</span></label>
          <input id="lead-name" name="name" type="text" autoComplete="name" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} className={`mt-1.5 ${inputClass}`} />
        </div>
        <div>
          <label htmlFor="lead-company" className="text-sm font-semibold text-slate-900">Company <span className="font-normal text-slate-500">(optional)</span></label>
          <input id="lead-company" name="company" type="text" autoComplete="organization" maxLength={120} value={company} onChange={(event) => setCompany(event.target.value)} className={`mt-1.5 ${inputClass}`} />
        </div>
      </div>
      {/* Honeypot: hidden from people, filled in by some bots. */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="lead-website">Website</label>
        <input id="lead-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="min-h-[52px] w-full rounded-xl bg-[#1a4e94] px-5 font-semibold text-white transition hover:bg-[#153f78] disabled:cursor-wait disabled:opacity-70"
      >
        {status === 'submitting' ? 'Getting your copy...' : 'Get the free PDF'}
      </button>
      <p id="lead-privacy" className="text-[13px] leading-5 text-slate-500">
        Your details go to Linus Tse at Greyfield Data, who may follow up about the paper. See the{' '}
        <Link to="/privacy" className="text-[#1a4e94] underline underline-offset-2">privacy policy</Link>.
      </p>
    </form>
  );
}

export default function WhitePaperPage({ slug }: { slug: string }) {
  const paper = getWhitePaper(slug);
  if (!paper) return <NotFoundPage />;
  const related = getWhitePaper(paper.related);
  const url = `${SITE_URL}${paper.path}`;

  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-['Inter']">
      <SEO title={paper.seoTitle} description={paper.seoDescription} url={url} image={`${SITE_URL}${paper.cover}`} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: paper.title, url },
        ]}
      />
      <Navigation />

      <main className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-8">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
          <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
          <span aria-hidden="true" className="mx-2">/</span>
          <span className="text-slate-700">White paper</span>
        </nav>

        <div className="mt-6 grid items-start gap-8 lg:mt-7 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-16">
          <header className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1a4e94] sm:text-[13px]">Free white paper · {paper.pages}-page PDF</p>
            <h1 className="mt-3 font-display text-[34px] font-bold leading-[1.12] tracking-tight text-[#0a1628] sm:text-5xl lg:text-[52px] lg:leading-[1.08]">{paper.title}</h1>
            <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-slate-600 sm:mt-5 sm:text-xl">{paper.lede}</p>
          </header>

          <section aria-labelledby="get-heading" className="relative rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_60px_-40px_rgba(10,22,40,0.55)] sm:p-8 lg:row-span-2">
            <div className="flex items-center gap-4 sm:gap-5">
              <img src={paper.cover} alt={paper.coverAlt} width={816} height={1056} className="h-auto w-[88px] flex-none rounded-md border border-slate-200 shadow-[0_10px_24px_-14px_rgba(10,22,40,0.5)] sm:w-[118px]" />
              <div className="min-w-0">
                <h2 id="get-heading" className="font-display text-xl font-bold leading-tight text-[#0a1628] sm:text-[22px]">Get the white paper</h2>
                <p className="mt-1.5 text-sm text-slate-500">PDF, {paper.pages} pages, {paper.size}</p>
              </div>
            </div>
            <LeadForm paper={paper} />
          </section>

          <section aria-labelledby="inside-heading" className="min-w-0">
            <h2 id="inside-heading" className="font-display text-[22px] font-bold text-[#0a1628] sm:text-2xl">What's inside</h2>
            <div className="mt-4 grid gap-3 sm:mt-5 sm:grid-cols-2 sm:gap-4">
              {paper.inside.map((item) => (
                <div key={item.heading} className="rounded-2xl border border-[#dbe7f7] bg-[#f7faff] p-[18px] sm:p-[22px]">
                  <h3 className="font-semibold text-[#0a1628]">{item.heading}</h3>
                  <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600 sm:mt-2">{item.body}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-12 grid gap-10 border-t border-slate-200 pt-10 lg:mt-20 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-16 lg:pt-14">
          <section aria-labelledby="contents-heading" className="min-w-0">
            <h2 id="contents-heading" className="font-display text-[22px] font-bold text-[#0a1628] sm:text-2xl">Contents</h2>
            <ol className="mt-3 text-[15px] text-slate-700 sm:mt-5 sm:grid sm:grid-flow-col sm:grid-cols-2 sm:gap-x-10" style={{ gridTemplateRows: `repeat(${Math.ceil(paper.contents.length / 2)}, auto)` }}>
              {paper.contents.map(([section, page]) => (
                <li key={section} className="flex justify-between gap-3 border-b border-slate-100 py-[11px]">
                  <span>{section}</span>
                  <span className="text-slate-500">
                    <span className="sr-only">page </span>{page}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          {related && (
            <aside aria-label="Another free white paper" className="flex items-center gap-4 self-start rounded-3xl bg-[#0a1628] p-5 text-white sm:gap-5 sm:p-7">
              <img src={related.cover} alt="" width={816} height={1056} loading="lazy" className="h-auto w-[68px] flex-none rounded sm:w-[84px]" />
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-300">Also free</p>
                <p className="mt-1.5 font-semibold leading-snug sm:text-[17px]">{related.title}</p>
                <Link to={related.path} className="mt-2 inline-block text-[15px] font-semibold text-blue-200 underline underline-offset-4 hover:text-white">
                  Get the {related.slug === 'customer-retention-reporting' ? 'retention' : 'data stack'} white paper
                </Link>
              </div>
            </aside>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
